import React, { useEffect, useState, useCallback } from 'react';
import { Landmark, Upload } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import opsService from '../../../services/opsService';

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Always return a STRING for an axios error. FastAPI 422s put an ARRAY of
// {loc,msg,...} objects in detail — rendering that array as a React child
// crashes the page, so coerce it here.
const errText = (err) => {
  const d = err?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d.map((e) => {
      const field = Array.isArray(e?.loc) ? e.loc[e.loc.length - 1] : null;
      return field ? `${field}: ${e.msg}` : (e?.msg || JSON.stringify(e));
    }).join('; ');
  }
  if (d && typeof d === 'object') return d.msg || JSON.stringify(d);
  return err?.message || 'Request failed';
};

function ImportPanel({ accounts, categories, onChanged }) {
  const [accountId, setAccountId] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({ amount_mode: 'signed', sign_rule: 'raw_is_inflow', date_format: '%Y-%m-%d' });
  const [msg, setMsg] = useState(null);
  const account = accounts.find((a) => String(a.id) === String(accountId));

  // Fetch the header preview (mapping form) for an unmapped account + file.
  const loadPreview = async (accId, f) => {
    if (!accId || !f) return;
    const acc = accounts.find((a) => String(a.id) === String(accId));
    if (!acc || acc.has_mapping) { setPreview(null); return; }
    try { setPreview(await opsService.financePreview(accId, f)); }
    catch (err) { setPreview(null); setMsg(errText(err)); }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0]; setFile(f); setPreview(null); setMsg(null);
    loadPreview(accountId, f);
  };

  const onAccountChange = (e) => {
    const id = e.target.value; setAccountId(id); setPreview(null); setMsg(null);
    loadPreview(id, file);  // re-show mapping if a file is already chosen
  };

  // amount columns required for the chosen mode
  const amountMapped = mapping.amount_mode === 'debit_credit'
    ? (mapping.debit_col || mapping.credit_col)
    : mapping.amount_col;
  const mappingComplete = mapping.date_col && mapping.description_col && amountMapped;

  const doImport = async () => {
    if (!accountId || !file) { setMsg('Pick an account and a file.'); return; }
    try {
      if (account && !account.has_mapping) {
        if (!preview) { await loadPreview(accountId, file); setMsg('Map your columns below, then click Import again.'); return; }
        if (!mappingComplete) { setMsg('Map the date, description, and amount column(s) below first.'); return; }
        await opsService.financeSetMapping(accountId, mapping);
      }
      const r = await opsService.financeImport(accountId, file);
      setMsg(`Imported ${r.imported}, skipped ${r.skipped} dup, auto-categorized ${r.auto_categorized}, uncategorized ${r.uncategorized}.`);
      setPreview(null); setFile(null); onChanged();
    } catch (err) { setMsg(errText(err)); }
  };
  const headers = preview?.headers || [];
  const colSelect = (key, label) => (
    <label className="text-small text-ink-muted flex flex-col">{label}
      <select className="border rounded px-2 py-1 border-border-strong" value={mapping[key] || ''} onChange={(e) => setMapping({ ...mapping, [key]: e.target.value })}>
        <option value="">—</option>{headers.map((h) => <option key={h} value={h}>{h}</option>)}
      </select>
    </label>
  );

  return (
    <div className="bg-surface-card rounded-card shadow-elevation-1 border p-4 space-y-3">
      <div className="font-medium text-ink-soft flex items-center gap-2"><Upload className="w-4 h-4" /> Import statement</div>
      <div className="flex flex-wrap gap-3 items-end">
        <select className="border rounded px-2 py-2 text-small border-border-strong" value={accountId} onChange={onAccountChange}>
          <option value="">Select account…</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.name} ({a.type}){a.has_mapping ? '' : ' — needs mapping'}</option>)}
        </select>
        <input type="file" accept=".csv" onChange={onFile} className="text-small" />
        <button onClick={doImport} disabled={!accountId || !file} className="px-3 py-2 bg-accent text-white rounded-lg text-small disabled:opacity-50">Import</button>
      </div>
      {preview && (
        <div className="border rounded p-3 space-y-2">
          <div className="text-small text-ink-muted">First-time mapping for this account ({preview.row_count} rows). Map your columns:</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {colSelect('date_col', 'Date column')}
            {colSelect('description_col', 'Description column')}
            <label className="text-small text-ink-muted flex flex-col">Amount mode
              <select className="border rounded px-2 py-1 border-border-strong" value={mapping.amount_mode} onChange={(e) => setMapping({ ...mapping, amount_mode: e.target.value })}>
                <option value="signed">Single signed amount</option><option value="debit_credit">Separate debit/credit</option>
              </select>
            </label>
            {mapping.amount_mode === 'signed' ? colSelect('amount_col', 'Amount column') : (<>{colSelect('debit_col', 'Debit column')}{colSelect('credit_col', 'Credit column')}</>)}
            {mapping.amount_mode === 'signed' && (
              <label className="text-small text-ink-muted flex flex-col">Sign rule
                <select className="border rounded px-2 py-1 border-border-strong" value={mapping.sign_rule} onChange={(e) => setMapping({ ...mapping, sign_rule: e.target.value })}>
                  <option value="raw_is_inflow">Positive = money in (most checking)</option>
                  <option value="raw_is_outflow">Positive = money out (most credit cards)</option>
                </select>
              </label>
            )}
            <label className="text-small text-ink-muted flex flex-col">Date format (strptime)
              <input className="border rounded px-2 py-1 border-border-strong" value={mapping.date_format} onChange={(e) => setMapping({ ...mapping, date_format: e.target.value })} placeholder="%Y-%m-%d" />
            </label>
            {colSelect('balance_col', 'Balance column (optional)')}
          </div>
        </div>
      )}
      {msg && <div className="text-small text-ink-muted">{typeof msg === 'string' ? msg : String(msg)}</div>}
    </div>
  );
}

function TransactionsPanel({ accounts, categories, refreshKey, onChanged }) {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState({ account_id: '', uncategorized: false, q: '' });
  const load = useCallback(async () => {
    const params = {};
    if (filter.account_id) params.account_id = filter.account_id;
    if (filter.uncategorized) params.uncategorized = true;
    if (filter.q) params.q = filter.q;
    setRows(await opsService.financeTransactions(params).catch(() => []));
  }, [filter]);
  useEffect(() => { load(); }, [load, refreshKey]);

  const setCat = async (id, category_id) => {
    if (!category_id) return;   // "Uncategorized" is display-only in v1 — can't clear via COALESCE update
    await opsService.financeSetTxnCategory(id, { category_id: Number(category_id) });
    load(); onChanged();
  };
  const toggleTransfer = async (t) => { await opsService.financeSetTxnCategory(t.id, { is_transfer: !t.is_transfer }); load(); onChanged(); };

  return (
    <div className="bg-surface-card rounded-card shadow-elevation-1 border overflow-hidden">
      <div className="px-4 py-2 border-b flex flex-wrap gap-3 items-center">
        <span className="font-medium text-ink-soft">Transactions</span>
        <select className="border rounded px-2 py-1 text-small border-border-strong" value={filter.account_id} onChange={(e) => setFilter({ ...filter, account_id: e.target.value })}>
          <option value="">All accounts</option>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <label className="text-small text-ink-muted flex items-center gap-1"><input type="checkbox" checked={filter.uncategorized} onChange={(e) => setFilter({ ...filter, uncategorized: e.target.checked })} /> Uncategorized only</label>
        <input className="border rounded px-2 py-1 text-small border-border-strong" placeholder="Search" value={filter.q} onChange={(e) => setFilter({ ...filter, q: e.target.value })} />
      </div>
      <table className="w-full text-small">
        <thead className="bg-surface-muted text-ink-muted"><tr>
          <th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Account</th>
          <th className="text-left px-3 py-2">Description</th><th className="text-right px-3 py-2">Amount</th>
          <th className="text-left px-3 py-2">Category</th><th className="px-3 py-2">Transfer</th>
        </tr></thead>
        <tbody>
          {rows.map((t) => (
            <tr key={t.id} className="border-t">
              <td className="px-3 py-2">{t.txn_date}</td>
              <td className="px-3 py-2">{t.account_name}</td>
              <td className="px-3 py-2 text-ink-soft">{t.description}</td>
              <td className={`px-3 py-2 text-right ${t.amount < 0 ? 'text-danger' : 'text-success'}`}>{money(t.amount)}</td>
              <td className="px-3 py-2">
                <select className="border rounded px-1 py-0.5 border-border-strong" value={t.category_id || ''} onChange={(e) => setCat(t.id, e.target.value)}>
                  <option value="">Uncategorized</option>
                  {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </td>
              <td className="px-3 py-2 text-center"><input type="checkbox" checked={t.is_transfer} onChange={() => toggleTransfer(t)} /></td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan="6" className="px-3 py-6 text-center text-ink-muted">No transactions — import a statement above.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function RulesPanel({ categories, refreshKey, onChanged }) {
  const [rules, setRules] = useState([]);
  const [draft, setDraft] = useState({ pattern: '', match_type: 'contains', category_id: '' });
  const [msg, setMsg] = useState(null);
  const load = useCallback(async () => { setRules(await opsService.financeRules().catch(() => [])); }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const add = async () => {
    if (!draft.pattern.trim() || !draft.category_id) { setMsg('Enter a keyword and pick a category.'); return; }
    try {
      await opsService.financeCreateRule({ pattern: draft.pattern.trim(), match_type: draft.match_type, category_id: Number(draft.category_id) });
      setDraft({ pattern: '', match_type: 'contains', category_id: '' }); setMsg(null); load(); onChanged();
    } catch (err) { setMsg(errText(err)); }
  };
  const del = async (id) => { try { await opsService.financeDeleteRule(id); load(); onChanged(); } catch (err) { setMsg(errText(err)); } };

  return (
    <div className="bg-surface-card rounded-card shadow-elevation-1 border p-4 space-y-3">
      <div className="font-medium text-ink-soft">Auto-categorization rules <span className="text-ink-muted text-small font-normal">— applied to future imports</span></div>
      <div className="flex flex-wrap gap-2 items-end">
        <label className="text-small text-ink-muted flex flex-col">If description
          <select className="border rounded px-2 py-1 border-border-strong" value={draft.match_type} onChange={(e) => setDraft({ ...draft, match_type: e.target.value })}>
            <option value="contains">contains</option><option value="equals">equals</option>
          </select>
        </label>
        <input className="border rounded px-2 py-1 text-small border-border-strong" placeholder="keyword (e.g. SHELL)" value={draft.pattern} onChange={(e) => setDraft({ ...draft, pattern: e.target.value })} />
        <label className="text-small text-ink-muted flex flex-col">then category
          <select className="border rounded px-2 py-1 border-border-strong" value={draft.category_id} onChange={(e) => setDraft({ ...draft, category_id: e.target.value })}>
            <option value="">Select…</option>
            {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </label>
        <button onClick={add} className="px-3 py-2 bg-accent text-white rounded-lg text-small">Add rule</button>
      </div>
      {msg && <div className="text-small text-danger">{typeof msg === 'string' ? msg : String(msg)}</div>}
      {rules.length > 0 && (
        <table className="w-full text-small">
          <tbody>
            {rules.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="px-2 py-1 text-ink-muted">description {r.match_type}</td>
                <td className="px-2 py-1 text-ink-soft font-medium">"{r.pattern}"</td>
                <td className="px-2 py-1 text-ink-muted">→</td>
                <td className="px-2 py-1 text-ink-soft">{r.category_name}</td>
                <td className="px-2 py-1 text-right"><button className="text-danger hover:underline" onClick={() => del(r.id)}>remove</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {rules.length === 0 && <div className="text-small text-ink-muted">No rules yet. Add keyword → category rules so recurring merchants auto-categorize on import.</div>}
    </div>
  );
}

function Card({ label, value, sub, tone }) {
  return (
    <div className="bg-surface-card rounded-card shadow-elevation-1 border p-4">
      <div className="text-ink-muted text-small">{label}</div>
      <div className={`text-2xl font-bold ${tone || 'text-ink-soft'}`}>{value}</div>
      {sub && <div className="text-ink-muted text-small mt-1">{sub}</div>}
    </div>
  );
}

function SummaryViews({ refreshKey }) {
  const [s, setS] = useState(null);
  useEffect(() => { opsService.financeSummary().then(setS).catch(() => setS(null)); }, [refreshKey]);
  if (!s) return null;
  const pnlTone = s.pnl.net >= 0 ? 'text-success' : 'text-danger';
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card label="Money in" value={money(s.totals.inflow)} tone="text-success" />
        <Card label="Money out" value={money(s.totals.outflow)} tone="text-danger" />
        <Card label="Net cash flow" value={money(s.totals.net)} tone={s.totals.net >= 0 ? 'text-success' : 'text-danger'} />
        <Card label="Net profit (P&L)" value={money(s.pnl.net)} tone={pnlTone}
              sub={s.pnl.margin != null ? `margin ${(s.pnl.margin * 100).toFixed(0)}%` : `${s.totals.uncategorized_count} uncategorized`} />
      </div>

      <div className="bg-surface-card rounded-card shadow-elevation-1 border p-4">
        <div className="font-medium text-ink-soft mb-2">Cash flow by month</div>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={s.cash_flow}>
            <XAxis dataKey="month" fontSize={12} /><YAxis fontSize={12} /><Tooltip formatter={(v) => money(v)} /><Legend />
            <Bar dataKey="inflow" name="In" fill="#16a34a" /><Bar dataKey="outflow" name="Out" fill="#dc2626" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-surface-card rounded-card shadow-elevation-1 border overflow-hidden">
          <div className="px-4 py-2 font-medium text-ink-soft border-b">Expenses by category</div>
          <table className="w-full text-small"><tbody>
            {s.by_category.map((c) => (
              <tr key={c.category} className="border-t"><td className="px-4 py-2 text-ink-soft">{c.category}</td><td className="px-4 py-2 text-right">{money(c.total)}</td></tr>
            ))}
            {s.by_category.length === 0 && <tr><td className="px-4 py-6 text-center text-ink-muted">No categorized expenses yet.</td></tr>}
          </tbody></table>
        </div>
        <div className="bg-surface-card rounded-card shadow-elevation-1 border overflow-hidden">
          <div className="px-4 py-2 font-medium text-ink-soft border-b">Top merchants</div>
          <table className="w-full text-small"><tbody>
            {s.top_merchants.map((m, i) => (
              <tr key={i} className="border-t"><td className="px-4 py-2 text-ink-soft truncate max-w-[14rem]">{m.description}</td><td className="px-4 py-2 text-right">{money(m.total)}</td><td className="px-4 py-2 text-right text-ink-muted">{m.count}×</td></tr>
            ))}
            {s.top_merchants.length === 0 && <tr><td className="px-4 py-6 text-center text-ink-muted">No spend yet.</td></tr>}
          </tbody></table>
        </div>
      </div>

      <div className="bg-surface-card rounded-card shadow-elevation-1 border overflow-hidden">
        <div className="px-4 py-2 font-medium text-ink-soft border-b">By account</div>
        <table className="w-full text-small">
          <thead className="bg-surface-muted text-ink-muted"><tr><th className="text-left px-4 py-2">Account</th><th className="text-left px-4 py-2">Type</th><th className="text-right px-4 py-2">In</th><th className="text-right px-4 py-2">Out</th><th className="text-right px-4 py-2">Txns</th><th className="text-left px-4 py-2">Last</th></tr></thead>
          <tbody>
            {s.by_account.map((a) => (
              <tr key={a.account_id} className="border-t"><td className="px-4 py-2 text-ink-soft">{a.name}</td><td className="px-4 py-2">{a.type}</td><td className="px-4 py-2 text-right text-success">{money(a.inflow)}</td><td className="px-4 py-2 text-right text-danger">{money(a.outflow)}</td><td className="px-4 py-2 text-right">{a.txn_count}</td><td className="px-4 py-2">{a.last_txn_date || '—'}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function OpsFinances() {
  const [accounts, setAccounts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const bump = () => setRefreshKey((k) => k + 1);

  const loadMeta = useCallback(async () => {
    setAccounts(await opsService.financeAccounts().catch(() => []));
    setCategories(await opsService.financeCategories().catch(() => []));
  }, []);
  useEffect(() => { loadMeta(); }, [loadMeta, refreshKey]);

  const addAccount = async () => {
    const name = window.prompt('Account name? (e.g. Business Checking)'); if (!name) return;
    const type = window.prompt('Type: checking or credit_card', 'checking');
    if (type !== 'checking' && type !== 'credit_card') { alert('Type must be checking or credit_card'); return; }
    try { await opsService.financeCreateAccount({ name, type }); bump(); }
    catch (err) { alert(errText(err)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-ink-soft flex items-center gap-2"><Landmark className="w-5 h-5" /> Finances</h3>
        <button onClick={addAccount} className="px-3 py-2 text-small border rounded-lg hover:bg-surface-muted">+ Add account</button>
      </div>
      <SummaryViews refreshKey={refreshKey} />
      <ImportPanel accounts={accounts} categories={categories} onChanged={bump} />
      <RulesPanel categories={categories} refreshKey={refreshKey} onChanged={bump} />
      <TransactionsPanel accounts={accounts} categories={categories} refreshKey={refreshKey} onChanged={bump} />
    </div>
  );
}
